import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useTheme, Fade } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Transaction, TransactionIssueType } from '../../../../services/transaction.types';
import { formatCurrency } from '../../../../utils/formatters';
import TransactionForm from './TransactionForm';
import { useAuth } from '../../../../context/AuthContext';
import ResponsiveTooltip from '../../../ResponsiveTooltip';

interface TransactionModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: Partial<Transaction>) => void;
    transaction: Transaction | null;
}

const TransactionModal: React.FC<TransactionModalProps> = ({ open, onClose, onSubmit, transaction }) => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    const handleFormSubmit = (values: Partial<Transaction>) => {
        onSubmit(values);
        onClose();
    };

    const getIssueMessages = () => {
        if (!transaction?.issueTypes || transaction.issueTypes.length === 0) return [];
        
        const messages: Array<{ severity: 'warning' | 'error', title: string, message: string }> = [];
        
        transaction.issueTypes.forEach((issueType) => {
            if (issueType === TransactionIssueType.NoIssue) return;

            switch (issueType) {
                case TransactionIssueType.MissingCostBasis:
                    const spendValue = transaction.sellValueInFiat || 0;
                    messages.push({
                        severity: 'error',
                        title: t('transactions.modal.issues.missingCostBasis.title'),
                        message: t('transactions.modal.issues.missingCostBasis.message', { 
                            spendValue: formatCurrency(spendValue, currentCtUser?.currency || '') 
                        }),
                    });
                    break;
                case TransactionIssueType.MissingPrice:
                    messages.push({
                        severity: 'warning',
                        title: t('transactions.modal.issues.missingPrice.title'),
                        message: t('transactions.modal.issues.missingPrice.message'),
                    });
                    break;
            }
        });
        
        return messages;
    };

    const issueMessages = getIssueMessages();

    const [isSaveDisabled, setIsSaveDisabled] = useState(transaction ? false : true);

    const handleSaveDisabledChange = (state: boolean) => {
        setIsSaveDisabled(state);
    };

    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{
                timeout: 200
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
            PaperProps={{
                sx: { 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    bgcolor: theme => theme.palette.background.paper,
                    '& .MuiDialogContent-root': {
                        bgcolor: theme => theme.palette.background.paper,
                    }
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                pt: 2,
                px: 3
            }}>
                <Typography variant="h6" component="div" sx={{
                    fontWeight: 600,
                    mb: 0.5
                }}>
                     {transaction ? t('transactions.modal.editTitle') : t('transactions.modal.createTitle')}
                </Typography>
                {/* Removing the subtitle to avoid duplication */}
            </DialogTitle>

            <DialogContent sx={{
                p: 3,
                pt: 2
            }}>
                {issueMessages.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {issueMessages.map((issue, index) => (
                            <Alert key={index} severity={issue.severity} sx={{
                                mb: index < issueMessages.length - 1 ? 2 : 0
                            }}>
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        component="div"
                                        sx={{
                                            mb: 0.5
                                        }}
                                    >
                                        {issue.title}
                                    </Typography>
                                    <Typography variant="body2">{issue.message}</Typography>
                                </Box>
                            </Alert>
                        ))}
                    </Box>
                )}
                <TransactionForm
                    transaction={transaction}
                    onSubmit={handleFormSubmit}
                    onSaveDisabledChange={handleSaveDisabledChange}
                />
            </DialogContent>

            <DialogActions sx={{
                px: 3,
                py: 2,
                justifyContent: 'space-between'
            }}>
                <Button onClick={onClose} variant="outlined">
                     {t('transactions.modal.cancel')}
                </Button>
                <ResponsiveTooltip 
                    title={isSaveDisabled ? t('transactions.modal.fillAllFields') : ''}
                    arrow
                    placement="top"
                >
                    <span>
                        <Button
                            onClick={() => {
                                const formElement = document.querySelector('form');
                                if (formElement) {
                                    formElement.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                                }
                            }}
                            variant="contained"
                            disabled={isSaveDisabled}
                        >
                            {transaction ? t('transactions.modal.saveChanges') : t('transactions.modal.createTransaction')}
                        </Button>
                    </span>
                </ResponsiveTooltip>
            </DialogActions>
        </Dialog>
    );
};

export default TransactionModal;
